import { arrayToMap, grantAllPermissions, mapToArray, normalizePermissionMap } from "../security/permissionRegistry";
import { localApiClient } from "./localApiClient";

const currentHospitalId = () =>
  sessionStorage.getItem("hospitalId") ||
  localStorage.getItem("hospitalId") ||
  "";

const wrapLocalError = (action, error) => {
  const message = error?.message || String(error);
  if (/bearer|jwt|unauthor/i.test(message)) throw new Error(`${action} failed: Local API token is missing or expired.`);
  if (/hospital|scope|forbidden/i.test(message)) throw new Error(`${action} failed: Hospital scope mismatch.`);
  if (/failed to fetch|network/i.test(message)) throw new Error(`${action} failed: Local API is unavailable.`);
  throw new Error(`${action} failed: ${message}`);
};

const permissionKeysFromRole = (role = {}) => {
  if (role.permissionsV2 && typeof role.permissionsV2 === "object") return toBackendPermissionKeys(mapToArray(normalizePermissionMap(role.permissionsV2)));
  if (Array.isArray(role.permissionKeys)) return role.permissionKeys;
  if (Array.isArray(role.permissions)) return toBackendPermissionKeys(role.permissions);
  return [];
};

const toBackendPermissionKeys = (frontendKeys = []) => {
  const out = new Set();
  const add = (...items) => items.filter(Boolean).forEach((item) => out.add(item));
  frontendKeys.forEach((key) => {
    switch (key) {
      case "patients.view":
        add("patients.read");
        break;
      case "patients.create":
        add("patients.create", "patients.read");
        break;
      case "patients.edit":
        add("patients.update", "patients.read");
        break;
      case "appointments.view":
        add("appointments.read");
        break;
      case "appointments.create":
      case "appointments.edit":
        add("appointments.manage", "appointments.read");
        break;
      case "emergency.view":
        add("ed.visits.read", "ed.dashboard.read", "ed.reports.read");
        break;
      case "emergency.create":
        add("ed.visits.create", "ed.visits.read", "ed.triage.create", "ed.notes.create", "ed.dashboard.read");
        break;
      case "emergency.edit":
        add("ed.notes.create", "ed.orders.create", "ed.disposition.manage", "ed.visits.read", "ed.dashboard.read");
        break;
      case "emergency.delete":
        add("ed.disposition.manage", "ed.visits.read");
        break;
      case "emergency.export":
        add("ed.reports.read");
        break;
      case "consultations.view":
        add("consultations.read");
        break;
      case "consultations.create":
        add("consultations.open", "clinical-requests.create", "consultations.read");
        break;
      case "consultations.edit":
        add("consultations.update", "vitals.record", "clinical-requests.create", "nursing.notes.read", "doctorStation.notes.read", "consultations.read");
        break;
      case "nursing.view":
        add("nursing.notes.read", "nursing.dashboard.read");
        break;
      case "nursing.create":
        add("nursing.notes.create", "nursing.notes.read", "nursing.dashboard.read");
        break;
      case "nursing.edit":
        add("nursing.notes.update", "nursing.tasks.manage", "nursing.carePlans.manage", "nursing.notes.read", "nursing.dashboard.read");
        break;
      case "nursing.delete":
        add("nursing.notes.cancel", "nursing.notes.read");
        break;
      case "doctor-notes.view":
        add("doctorStation.notes.read");
        break;
      case "doctor-notes.create":
        add("doctorStation.notes.create", "doctorStation.notes.read");
        break;
      case "doctor-notes.edit":
        add("doctorStation.notes.update", "doctorStation.notes.read");
        break;
      case "doctor-notes.delete":
        add("doctorStation.notes.cancel", "doctorStation.notes.read");
        break;
      case "billing.view":
        add("billing.read", "invoices.read", "payments.read", "ledger.read");
        break;
      case "billing.create":
        add("billing.create", "billing.clinical-requests", "invoices.generate", "payments.process", "billing.read", "invoices.read", "payments.read");
        break;
      case "billing.edit":
      case "billing.delete":
        add("invoices.cancel", "payments.reverse", "financial.adjust", "billing.read", "invoices.read", "payments.read");
        break;
      case "lab.view":
        add("lab.read");
        break;
      case "lab.create":
      case "lab.edit":
        add("lab.sample", "lab.result", "lab.mcs.enter", "lab.mcs.read", "lab.read");
        break;
      case "lab.approve":
        add("lab.review", "lab.mcs.review", "lab.mcs.complete", "lab.mcs.read", "lab.read");
        break;
      case "radiology.view":
        add("radiology.read");
        break;
      case "radiology.create":
      case "radiology.edit":
        add("radiology.image-capture", "radiology.report", "radiology.read");
        break;
      case "radiology.approve":
        add("radiology.review", "radiology.read");
        break;
      case "pharmacy.view":
      case "inventory.view":
        add("pharmacy.read");
        break;
      case "inventory.create":
      case "inventory.edit":
      case "inventory.delete":
        add("pharmacy.inventory.register", "pharmacy.read");
        break;
      case "inventory.adjust":
        add("pharmacy.inventory.adjust", "pharmacy.read");
        break;
      case "pharmacy.create":
      case "pharmacy.edit":
        add("pharmacy.dispense", "pharmacy.read");
        break;
      case "pharmacy.delete":
        add("pharmacy.dispensing.cancel", "pharmacy.read");
        break;
      case "settings.view":
      case "settings.edit":
      case "departments.view":
      case "departments.create":
      case "departments.edit":
      case "clinics.view":
      case "clinics.create":
      case "clinics.edit":
        add("settings.manage", "clinics.manage", "services.manage");
        break;
      case "reports.view":
      case "reports.export":
        add("reports.read");
        break;
      case "staff.view":
      case "staff.create":
      case "staff.edit":
        add("users.manage");
        break;
      case "roles-permissions.view":
      case "roles-permissions.create":
      case "roles-permissions.edit":
        add("roles.manage");
        break;
      case "server-management.view":
        add("server.manage", "activation.manage");
        break;
      case "server-management.edit":
        add("server.manage", "activation.manage");
        break;
      case "sync-status.view":
        add("sync.manage");
        break;
      case "sync-status.edit":
        add("sync.manage");
        break;
      case "admin-audit.view":
      case "admin-audit.export":
        add("auditLogs.read", "financialCorrections.read", "adminAudit.read");
        break;
      default:
        if (/^[a-z-]+\.[a-z-]+$/.test(key)) add(key);
    }
  });
  return Array.from(out);
};

const toFrontendPermissionKeys = (keys = []) => {
  const out = new Set();
  const add = (...items) => items.filter(Boolean).forEach((item) => out.add(item));
  keys.forEach((key) => {
    switch (key) {
      case "*":
        add("*");
        break;
      case "patients.create":
        add("patients.view", "patients.create");
        break;
      case "patients.read":
        add("patients.view");
        break;
      case "patients.update":
        add("patients.view", "patients.edit");
        break;
      case "appointments.read":
        add("appointments.view");
        break;
      case "appointments.manage":
        add("appointments.view", "appointments.create", "appointments.edit");
        break;
      case "ed.visits.read":
      case "ed.dashboard.read":
      case "ed.reports.read":
        add("emergency.view");
        if (key === "ed.reports.read") add("emergency.export");
        break;
      case "ed.visits.create":
      case "ed.triage.create":
        add("emergency.view", "emergency.create");
        break;
      case "ed.notes.create":
      case "ed.orders.create":
      case "ed.disposition.manage":
        add("emergency.view", "emergency.edit");
        break;
      case "ed.orders.bill":
        add("emergency.view", "billing.view", "billing.create");
        break;
      case "consultations.open":
        add("consultations.view", "consultations.create");
        break;
      case "consultations.read":
        add("consultations.view");
        break;
      case "consultations.update":
      case "vitals.record":
      case "clinical-requests.create":
        add("consultations.view", "consultations.create", "consultations.edit");
        break;
      case "nursing.notes.read":
      case "nursing.dashboard.read":
        add("nursing.view");
        break;
      case "nursing.notes.create":
        add("nursing.view", "nursing.create");
        break;
      case "nursing.notes.update":
      case "nursing.tasks.manage":
      case "nursing.carePlans.manage":
        add("nursing.view", "nursing.edit");
        break;
      case "nursing.notes.cancel":
        add("nursing.view", "nursing.delete");
        break;
      case "doctorStation.notes.read":
        add("doctor-notes.view");
        break;
      case "doctorStation.notes.create":
        add("doctor-notes.view", "doctor-notes.create");
        break;
      case "doctorStation.notes.update":
        add("doctor-notes.view", "doctor-notes.edit");
        break;
      case "doctorStation.notes.cancel":
        add("doctor-notes.view", "doctor-notes.delete");
        break;
      case "billing.read":
      case "invoices.read":
      case "payments.read":
      case "ledger.read":
        add("billing.view");
        break;
      case "billing.create":
      case "billing.clinical-requests":
      case "invoices.generate":
      case "payments.process":
        add("billing.view", "billing.create", "billing.edit");
        break;
      case "invoices.cancel":
      case "payments.reverse":
      case "financial.adjust":
        add("billing.view", "billing.edit", "billing.delete");
        break;
      case "lab.read":
        add("lab.view");
        break;
      case "lab.sample":
      case "lab.result":
      case "lab.mcs.enter":
        add("lab.view", "lab.create", "lab.edit");
        break;
      case "lab.review":
      case "lab.mcs.review":
      case "lab.mcs.complete":
        add("lab.view", "lab.approve");
        break;
      case "lab.mcs.read":
        add("lab.view");
        break;
      case "lab.mcs.cancel":
        add("lab.view", "lab.delete");
        break;
      case "radiology.read":
        add("radiology.view");
        break;
      case "radiology.image-capture":
      case "radiology.report":
        add("radiology.view", "radiology.create", "radiology.edit");
        break;
      case "radiology.review":
        add("radiology.view", "radiology.approve");
        break;
      case "pharmacy.read":
        add("pharmacy.view", "inventory.view");
        break;
      case "pharmacy.inventory.manage":
        add("pharmacy.view", "inventory.view", "inventory.create", "inventory.edit", "inventory.adjust", "inventory.delete");
        break;
      case "pharmacy.inventory.register":
        add("pharmacy.view", "inventory.view", "inventory.create", "inventory.edit", "inventory.delete");
        break;
      case "pharmacy.inventory.adjust":
        add("pharmacy.view", "inventory.view", "inventory.adjust");
        break;
      case "pharmacy.dispense":
        add("pharmacy.view", "pharmacy.create", "pharmacy.edit");
        break;
      case "pharmacy.dispensing.cancel":
        add("pharmacy.view", "pharmacy.delete");
        break;
      case "services.manage":
      case "settings.manage":
      case "clinics.manage":
        add("settings.view", "settings.edit", "departments.view", "departments.create", "departments.edit", "clinics.view", "clinics.create", "clinics.edit");
        break;
      case "reports.read":
        add("reports.view", "reports.export");
        break;
      case "users.manage":
        add("staff.view", "staff.create", "staff.edit");
        break;
      case "roles.manage":
        add("roles-permissions.view", "roles-permissions.create", "roles-permissions.edit");
        break;
      case "activation.manage":
      case "server.manage":
        add("server-management.view", "server-management.edit");
        break;
      case "sync.manage":
        add("sync-status.view", "sync-status.edit");
        break;
      case "audit.read":
      case "auditLogs.read":
      case "financialCorrections.read":
      case "adminAudit.read":
        add("admin-audit.view", "admin-audit.export");
        break;
      default:
        add(key);
    }
  });
  return Array.from(out);
};

const fromLocalRole = (role = {}) => {
  const permissionKeys = Array.isArray(role.rolePermissions)
    ? role.rolePermissions.map((entry) => entry?.permission?.key).filter(Boolean)
    : permissionKeysFromRole(role);
  const permissionsV2 = permissionKeys.includes("*") ? grantAllPermissions() : arrayToMap(toFrontendPermissionKeys(permissionKeys));
  return {
    ...role,
    id: role.id,
    name: role.name || "",
    description: role.description || "",
    hospitalId: role.hospitalId || null,
    isPredefined: role.isSystem === true,
    isSystem: role.isSystem === true,
    permissions: permissionKeys,
    permissionKeys,
    permissionsV2,
    lastUpdated: role.updatedAt || role.createdAt || null,
  };
};

export const localPermissionService = {
  async getCurrentUserPermissions() {
    try {
      const data = await localApiClient.get("/users/me/permissions");
      const permissions = data?.permissions || data?.user?.permissions || [];
      return {
        ...data,
        permissions,
        permissionsV2: permissions.includes("*") ? grantAllPermissions() : arrayToMap(toFrontendPermissionKeys(permissions)),
      };
    } catch (error) {
      wrapLocalError("Fetch current permissions", error);
    }
  },

  async listPermissions() {
    try {
      return localApiClient.get("/permissions");
    } catch (error) {
      wrapLocalError("Fetch permissions", error);
    }
  },

  async ensureDefaultRoles(hospitalId = currentHospitalId()) {
    try {
      return localApiClient.post(`/roles/${encodeURIComponent(hospitalId)}/defaults`, {});
    } catch (error) {
      wrapLocalError("Seed default roles", error);
    }
  },

  async fetchCustomRoles(hospitalId = currentHospitalId()) {
    try {
      const rows = await localApiClient.get(`/roles/${encodeURIComponent(hospitalId)}`);
      return (Array.isArray(rows) ? rows : []).filter((role) => !role.isSystem).map(fromLocalRole);
    } catch (error) {
      wrapLocalError("Fetch custom roles", error);
    }
  },

  async fetchPredefinedRoles(hospitalId = currentHospitalId()) {
    try {
      const rows = await localApiClient.get(`/roles/${encodeURIComponent(hospitalId)}`);
      return (Array.isArray(rows) ? rows : []).filter((role) => role.isSystem).map(fromLocalRole);
    } catch (error) {
      wrapLocalError("Fetch predefined roles", error);
    }
  },

  async saveCustomRoleToFirestore(hospitalId, role = {}) {
    try {
      const permissionKeys = permissionKeysFromRole(role);
      if (role.id) {
        await localApiClient.patch(`/roles/${encodeURIComponent(hospitalId)}/${encodeURIComponent(role.id)}`, {
          name: role.name,
          description: role.description,
        });
        await localApiClient.post(`/roles/${encodeURIComponent(hospitalId)}/${encodeURIComponent(role.id)}/permissions`, {
          permissionKeys,
        });
        return { success: true, message: "Role saved successfully.", id: role.id };
      }
      const saved = await localApiClient.post(`/roles/${encodeURIComponent(hospitalId)}`, {
        name: role.name,
        description: role.description,
        permissionKeys,
      });
      return { success: true, message: "Role saved successfully.", id: saved.id, data: fromLocalRole(saved) };
    } catch (error) {
      wrapLocalError("Save role", error);
    }
  },

  async deleteCustomRole(roleId, hospitalId = currentHospitalId()) {
    try {
      await localApiClient.delete(`/roles/${encodeURIComponent(hospitalId)}/${encodeURIComponent(roleId)}`);
      return { success: true, message: "Role deleted successfully." };
    } catch (error) {
      wrapLocalError("Delete role", error);
    }
  },

  async fetchRolePermissions(hospitalId = currentHospitalId(), roleName) {
    try {
      const roles = await localApiClient.get(`/roles/${encodeURIComponent(hospitalId)}`);
      const role = (Array.isArray(roles) ? roles : []).find((item) => item.name === roleName);
      return role ? fromLocalRole(role).permissionKeys : [];
    } catch (error) {
      wrapLocalError("Fetch role permissions", error);
    }
  },

  async assignRoleToUser(hospitalId, userId, roleId) {
    try {
      return localApiClient.post(`/users/${encodeURIComponent(hospitalId)}/${encodeURIComponent(userId)}/roles`, { roleId });
    } catch (error) {
      wrapLocalError("Assign role to user", error);
    }
  },

  async setUserStatus(hospitalId, userId, disabled) {
    try {
      return localApiClient.patch(`/users/${encodeURIComponent(hospitalId)}/${encodeURIComponent(userId)}/status`, { disabled });
    } catch (error) {
      wrapLocalError("Update user status", error);
    }
  },

  async resetLocalUserPassword(hospitalId, userId, password) {
    try {
      return localApiClient.post(`/users/${encodeURIComponent(hospitalId)}/${encodeURIComponent(userId)}/reset-password`, { password });
    } catch (error) {
      wrapLocalError("Reset local user password", error);
    }
  },
};

export default localPermissionService;
